import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClientNote,
  createClientNote,
  updateClientNote,
} from "../../store/thunks/clientNotesThunk";

const ClientNotes = ({ client }) => {
  const dispatch = useDispatch();
  const [noteContent, setNoteContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', 'error'
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const note = useSelector((state) => state.clientNotes.note);
  const loading = useSelector((state) => state.clientNotes.loading);
  const error = useSelector((state) => state.clientNotes.error);

  useEffect(() => {
    dispatch(fetchClientNote(client._id));
  }, [client._id, dispatch]);

  // Set the latest content from history as the current note when note changes in the state
  useEffect(() => {
    const latestContent = note?.history?.slice(-1)[0]?.content || "";
    setNoteContent(latestContent);
  }, [note]);

  const handleNoteChange = (e) => setNoteContent(e.target.value);

  const handleSaveNote = async () => {
    if (!noteContent.trim()) return;
    setIsSubmitting(true);
    setSaveStatus('saving');

    try {
      if (note) {
        await dispatch(
          updateClientNote({ clientId: client._id, content: noteContent })
        ).unwrap();
      } else {
        await dispatch(
          createClientNote({ clientId: client._id, content: noteContent })
        ).unwrap();
      }
      setIsEditing(false);
      setSaveStatus('saved');
      setSuccessMessage(`Note updated successfully for ${client.firstName} ${client.lastName}`);
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        setSaveStatus(null);
      }, 2000);
      dispatch(fetchClientNote(client._id));
    } catch (error) {
      console.error("Error saving note:", error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    const latestContent = note?.history?.slice(-1)[0]?.content || "";
    setNoteContent(latestContent);
    setIsEditing(false);
  };

  const getSaveButtonText = () => {
    if (saveStatus === 'saving') return 'Saving...';
    if (saveStatus === 'saved') return 'Saved!';
    if (saveStatus === 'error') return 'Error - Try Again';
    return note ? "Save Note" : "Create Note";
  };

  const getSaveButtonClass = () => {
    const baseClass = "inline-flex items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background h-9 px-4 py-2 shadow-sm";
    
    if (saveStatus === 'saving') return `${baseClass} bg-gray-400 text-white cursor-not-allowed`;
    if (saveStatus === 'saved') return `${baseClass} bg-green-600 text-white`;
    if (saveStatus === 'error') return `${baseClass} bg-red-600 text-white hover:bg-red-500`;
    return `${baseClass} bg-gray-900 text-white hover:bg-gray-800`;
  };

  return (
    <div className="bg-white rounded-sm shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
          Notes for {client.firstName} {client.lastName}
        </h2>
        {!isEditing ? (
          <button
            onClick={handleEdit}
            className="inline-flex items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-gray-900 text-white hover:bg-gray-800 h-9 px-4 py-2 shadow-sm">
            {note ? "Edit Note" : "Create Note"}
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleSaveNote}
              disabled={isSubmitting || !noteContent.trim()}
              className={getSaveButtonClass()}>
              {getSaveButtonText()}
            </button>
            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              className="inline-flex items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-gray-100 text-gray-900 hover:bg-gray-200 h-9 px-4 py-2 shadow-sm">
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {error && error.error && (
          <div className="rounded-sm border border-red-200 bg-red-50 p-3">
            <div className="text-sm text-red-700">{error.error}</div>
          </div>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Note Content
          </label>
          <textarea
            className="flex w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[120px] resize-none"
            placeholder="Type your note here..."
            rows="6"
            value={noteContent}
            onChange={handleNoteChange}
            disabled={!isEditing || isSubmitting || loading}
          />
        </div>

        {note && note.history && note.history.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-sm font-medium text-gray-900 hover:text-gray-700 transition-colors flex items-center gap-1">
              {showHistory ? "Hide History" : "View History"}
              <svg className={`h-4 w-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showHistory && (
              <div className="mt-3 space-y-3">
                <h3 className="text-sm font-medium text-gray-900">Note History</h3>
                {note.history
                  .slice()
                  .reverse()
                  .map((historyItem, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-sm p-3 border border-gray-200">
                      <div className="flex justify-between items-start gap-x-4 mb-2">
                        <div className="text-xs text-gray-500">
                          <span className="font-medium text-gray-900">
                            {historyItem.modifiedBy?.firstName}{" "}
                            {historyItem.modifiedBy?.lastName}
                          </span>{" "}
                          modified
                        </div>
                        <time
                          dateTime={historyItem.modifiedAt}
                          className="text-xs text-gray-500">
                          {new Date(historyItem.modifiedAt).toLocaleString()}
                        </time>
                      </div>
                      <p className="text-sm text-gray-700">
                        {historyItem.content}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {note && note.history && note.history.length === 0 && (
          <div className="border-t border-gray-200 pt-4">
            <div className="text-sm text-gray-500">No history available.</div>
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{successMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientNotes;
